import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import {
  ExhibitionContainer,
  PageTitle,
  Byline,
  Paragraph
} from '../../styles/exhibitionStyles';

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%;
  margin-bottom: 30px;
  background: #000;
`;

const VideoFrame = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;
  margin: 30px 0 10px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryItem = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  width: 100%;
  text-align: left;
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
  display: block;
`;

export default function MapsOfHyperspaceDocumentary() {
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [stillImages, setStillImages] = useState([]);

  useEffect(() => {
    document.title = "Maps Of Hyperspace: Feature Length Documentary - SLEEPR";
  }, []);

  useEffect(() => {
    let isMounted = true;
    const candidates = Array.from({ length: 113 }, (_, index) => (
      `/img/documentary/stills/documentary-still-web-${index + 1}.jpg`
    ));

    Promise.all(
      candidates.map((src) => new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(src);
        img.onerror = () => resolve(null);
        img.src = src;
      }))
    ).then((results) => {
      if (!isMounted) return;
      setStillImages(results.filter(Boolean));
    });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <ExhibitionContainer>
      <VideoWrapper>
        <VideoFrame
          src="https://www.youtube.com/embed/fKR0-0OfE1E"
          title="Maps Of Hyperspace: Feature length documentary"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </VideoWrapper>

      <PageTitle>Maps Of Hyperspace: Feature Length Documentary</PageTitle>
      <Byline>
        A 65-minute documentary following Sleepr's immersion into the Peruvian Amazon.
      </Byline>

      <Paragraph>
        This 65-minute documentary follows the visual artist - Sleepr - and their immersive journey into the Peruvian Amazon, where he undergoes twelve traditional ayahuasca ceremonies guided by Shipibo shamans. His aim is to directly observe and document the visual architecture of the ayahuasca experience—not through interpretation or metaphor, but by attempting to faithfully capture its aesthetic and perceptual qualities in visual art.
      </Paragraph>
      <Paragraph>
        As the ceremonies progress, the artist confronts increasingly complex visions and encounters with seemingly autonomous intelligences or “plant spirits,” challenging his assumptions about reality, consciousness, and agency. What begins as an observational effort deepens into a transformative process, dissolving conceptual frameworks and exposing the limitations of language and culture in describing direct experience.
      </Paragraph>
      <Paragraph>
        The resulting series of artworks, form a visual record of inner space—offering a rare window into one individuals structure, rhythm, and symbolic density of the visionary space. Combining ethnographic presence with artistic inquiry, the film explores the intersection of visionary states and aesthetic representation.
      </Paragraph>

      <GalleryGrid>
        {stillImages.map((image, index) => (
          <GalleryItem key={image} onClick={() => setLightboxIndex(index)} aria-label="Open still image">
            <GalleryImage src={image} alt={`Documentary still ${index + 1}`} />
          </GalleryItem>
        ))}
      </GalleryGrid>

      <Lightbox
        open={lightboxIndex >= 0}
        index={lightboxIndex}
        close={() => setLightboxIndex(-1)}
        controller={{ closeOnBackdropClick: true }}
        slides={stillImages.map((src) => ({ src }))}
      />
    </ExhibitionContainer>
  );
}
